<template>
    <div :id="'overview_' + uniqueIdentifier"
         class="overview"
         :class="{
             'overview--with-sticky-actions': actionsSticky,
             'overview--page-has-sticky-buttons': pageHasStickyButtonGroup,
             'overview--with-sticky-header': showStickyHeader && shouldShowStickyHeader,
             'overview--scrollable': tableIsScrollable,
         }"
    >
        <a id="top" />

        <component
            :is="filtersComponent"
            :id="'overview-filters_' + uniqueIdentifier"
            ref="filters"
            :loading="loading"
            :config="config"
            @reload="refresh()"
        >
            <slot name="filters" :data="currentData" />
        </component>

        <slot
            name="header"
            :active-filters="activeFilters"
            :data="currentData"
            :filters="config.filters"
            :search-query="searchQuery"
            :xhr="xhr"
        />

        <button v-if="resolvedUrl && config.groupsToggleButton"
                type="button"
                class="button button--text button--grouped overview__groups-toggle button--small"
                data-testid="overview:groups-toggle"
                @click="$refs.table.toggleExpandGroups()"
        >
            <span class="button__icon-group">
                <frm-icon :name="hasOpenedRows ? 'editor-compress' : 'editor-expand'" class="button__icon" />
                <span>{{ $tc('overview.toggle-groups', hasOpenedRows) }}</span>
            </span>
        </button>

        <template v-if="shouldShowStickyHeader">
            <a v-if="!tableIsScrollable" ref="stickyHeaderAnchor" href="#" class="overview__sticky-header-anchor" />
            <frm-overview-sticky-header
                v-show="showStickyHeader"
                class="overview__sticky-header"
            />
        </template>

        <component
            :is="tableComponent"
            v-if="resolvedUrl"
            :id="'overview-table_' + uniqueIdentifier"
            ref="table"
            :actions-disabled="actionsDisabled"
            :config="config"
            :url="resolvedUrl"
            :handle-action="handleAction"
            :scrollable="scrollable"
            :row-height="rowHeight"
            @deleteItem="deleteItem"
            @draw="$emit('draw') && $root.$emit('recalculateModalHeight') && updateCurrentData()"
            @editItem="editItem"
            @editPreferredItem="editPreferredItem"
            @reorderItem="reorderItem"
            @selectItems="selectItems"
            @groupingAction="handleGroupingAction($event)"
            @update="$root.$emit('recalculateModalHeight'); $emit('update')"
            @xhr="xhr = $event; $emit('xhr', $event)"
            @action="$emit('action', $event)"
            @xhrError="showError($event)"
            @before-refresh="$emit('before-refresh')"
            @opened-rows="hasOpenedRows = $event.length > 0"
            @opened-subtable-rows="openedSubtableRowIds = $event"
        />

        <a v-if="!tableIsScrollable" ref="actionsAnchor" href="#" class="overview__actions-anchor" />

        <component
            :is="actionsComponent"
            v-if="resolvedUrl"
            :id="'overview-actions_' + uniqueIdentifier"
            ref="actions"
            :config="config"
            :loading="actionIsLoading"
            :disabled="actionsDisabledComputed"
            :handle-action="handleActionsComponentAction"
            :sticky="actionsSticky"
            @action="$emit('action', $event)"
        >
            <slot name="actions" :data="currentData" :xhr="xhr" />
        </component>
    </div>
</template>

<script>
    import { mapMutations, mapState } from '../store/helpers';
    import axios from 'axios';

    import DialogManager from '../services/DialogManager';
    import CreatesOverviewState from '../mixins/Overview/CreatesState';
    import DefaultOverviewMixin from '../mixins/Overview/Default';
    import HandlesActions from '../mixins/Overview/HandlesActions';
    import HandlesShortcuts from '../mixins/HandlesShortcuts';
    import OverviewFilters from './Overview/Filters.vue';
    import OverviewTable from './Overview/Table.vue';
    import OverviewActions from './Overview/Actions.vue';
    import UrlQueryHelper from '../services/UrlQueryHelper';
    import OverviewStickyHeader from './Overview/StickyHeader.vue';
    import HandlesJobs from '../mixins/Overview/HandlesJobs';
    import HandlesErrorResponses from '../mixins/HandlesErrorResponses';

    export default {
        mixins: [CreatesOverviewState, DefaultOverviewMixin, HandlesActions, HandlesShortcuts, HandlesJobs, HandlesErrorResponses],

        components: {
            'frm-overview-actions': OverviewActions,
            'frm-overview-filters': OverviewFilters,
            'frm-overview-table': OverviewTable,
            'frm-overview-sticky-header': OverviewStickyHeader,
        },

        props: {
            config: {
                type: Object,
                required: true,
            },

            actionsDisabled: {
                type: Boolean,
                default: false,
            },

            actionsComponent: {
                type: String,
                default: 'frm-overview-actions',
            },

            filtersComponent: {
                type: String,
                default: 'frm-overview-filters',
            },

            tableComponent: {
                type: String,
                default: 'frm-overview-table',
            },

            scrollable: {
                type: Boolean,
                default: false,
            },

            rowHeight: {
                type: Number,
                default: 60,
            },
        },

        data() {
            return {
                reorderPromise: Promise.resolve(),
                currentData: [],
                xhr: null,
                actionsInViewport: true,
                pageHasStickyButtonGroup: false,
                showStickyHeader: false,
                hasOpenedRows: false,
                openedSubtableRowIds: [],
                resolvedUrl: this.config.url || null,
                inPanel: false,
            };
        },

        watch: {
            activeFilters() {
                this.$emit('filters-changed', this.activeFilters);
            },
        },

        computed: {
            ...mapState([
                'activeFilters', // N.B. Needed for HandlesActions
                'readonlyIds', // N.B. Needed for HandlesActions
                'selectedIds', // N.B. Needed for HandlesActions
                'selectedRowsData', // N.B. Needed for HandlesActions
                'uniqueIdentifier',
                'loading',
                'searchQuery',
            ]),

            showOnlyActiveShouldSearchNegative() {
                return this.config.showOnlyActiveShouldSearchNegative;
            },

            initialSearchQuery() {
                return this.config.initialSearchQuery;
            },

            actionsDisabledComputed() {
                return this.actionsDisabled || this.actionIsLoading;
            },

            actionsSticky() {
                if (this.actionsInViewport) {
                    return false;
                }

                if (this.inPanel) {
                    return false;
                }

                return !!this.config.buttons.length || this.actionsComponent !== 'frm-overview-actions';
            },

            shouldShowStickyHeader() {
                return !(new UrlQueryHelper()).isModal() && !this.inPanel;
            },

            tableIsScrollable() {
                return this.scrollable || this.config.endlessScrollable;
            },
        },

        mounted() {
            if (this.config.jobUrl) {
                this.fetchJob();
            }

            this.$shortcut.$on('overview.create', () => {
                if (!this.actionsDisabled && this.createAction && this.canHandleShortcuts()) {
                    this.handleAction(this.createAction);
                }
            });

            document.addEventListener('keydown', (e) => {
                if (e.key === 'Enter' && e.target.tagName !== 'INPUT' && e.target.tagName !== 'TEXTAREA' && !e.target.isContentEditable) {
                    this.editPreferredItem();
                }
            });

            if (!this.tableIsScrollable) {
                if (this.shouldShowStickyHeader) {
                    this.$options.stickyHeaderAnchorObserver = new IntersectionObserver(entries => {
                        this.showStickyHeader = entries[0].intersectionRatio === 0;
                    }, {
                        rootMargin: '-80px',
                        threshold: 0,
                    });

                    this.$options.stickyHeaderAnchorObserver.observe(this.$refs.stickyHeaderAnchor);
                }

                this.pageHasStickyButtonGroup = !!document.querySelectorAll('.form-button-group--sticky').length;

                this.$options.observer = new IntersectionObserver(entries => {
                    this.actionsInViewport = entries[0].intersectionRatio > 0;
                }, {
                    rootMargin: this.pageHasStickyButtonGroup ? '-50px' : '0px',
                    threshold: 0,
                });

                this.$options.observer.observe(this.$refs.actionsAnchor);
            }
            // Save filters to the shared state
            this.setFiltersAction(this.config.filters);

            this.inPanel = !!this.$el.closest('.panel');
        },

        beforeDestroy() {
            if (this.$options.observer) {
                this.$options.observer.disconnect();
                this.$options.observer = null;
            }

            if (this.$options.stickyHeaderAnchorObserver) {
                this.$options.stickyHeaderAnchorObserver.disconnect();
                this.$options.stickyHeaderAnchorObserver = null;
            }
        },

        methods: {
            ...mapMutations([
                'setFiltersAction',
                'setActiveFilterAction',
                'setReadonlyIds',
                'setSelectedIds',
                'setSelectedRowsData',
            ]),

            updateCurrentData() {
                this.currentData = this.$refs.table.getRowData();
            },

            selectItems(event) {
                this.setSelectedIds(event.ids);
                this.setSelectedRowsData(event.rowsData);
                this.setReadonlyIds(event.readonlyIds);
            },

            editItem() {
                if (this.editAction) {
                    this.handleAction(this.editAction);
                }
            },

            editPreferredItem() {
                const action = this.preferredAction || this.editAction || this.openAction;

                if (action) {
                    this.handleAction(action);
                }
            },

            deleteItem() {
                if (this.deleteAction) {
                    this.handleAction(this.deleteAction);
                }
            },

            reorderItem(event) {
                // execute reorder-actions in queue with a promise
                this.reorderPromise = this.reorderPromise.then((previousResult) => {
                    // check if the previous promise was successfull, otherwise skip request
                    if (previousResult === false) {
                        return Promise.resolve(false);
                    }

                    return axios.post(this.config.sortableAction, {
                        id: event.id,
                        position: event.position,
                        extraProperties: event.extraProperties,
                    }).then(() => {
                        // When we have a grouped config variable or there are extra properties, refresh  (currently used for reordering into a different group)
                        if (this.config.grouped || Object.keys(event.extraProperties).length > 0) {
                            this.$refs.table.refresh();
                        }
                        this.$emit('reordered-item');

                        return Promise.resolve(true);
                    }).catch(() => {
                        // when action failed, refresh the overview and show an error. Also cancel queued actions
                        this.$refs.table.refresh(() => {
                            DialogManager.alert(this.$t('base.errors.general'));
                            // reset the promise
                            this.reorderPromise = Promise.resolve();
                        });
                        // reject the current queue
                        return Promise.resolve(false);
                    });
                });
            },

            async refresh(callback = null) {
                if (this.config.jobUrl) {
                    await this.fetchJob();
                    return;
                }

                this.$refs.table.refresh(callback);
            },

            selectRow(id) {
                // DataTables reselects all selected rows after a refresh. However,
                // this callback is triggered before that, so we must use a setTimeout.
                setTimeout(() => {
                    this.$refs.table.selectRow(id);
                });
            },

            deselectAllRows() {
                this.$refs.table.deselectAndDraw();
                this.setSelectedIds([]);
                this.setSelectedRowsData([]);
                this.setReadonlyIds([]);
            },

            getRowCount() {
                return this.$refs.table.getRowCount();
            },

            setUrl(url) {
                this.config.url = url;
                this.$refs.table.setUrl(url);
            },

            setFilters(filters) {
                Object.keys(filters).forEach(attribute => {
                    this.setActiveFilterAction({ attribute, value: filters[attribute] });
                });
            },

            showError(errorMessage) {
                return DialogManager.alert(errorMessage.replaceAll('\n', '<br>'));
            },

            handleGroupingAction(groupingValue) {
                if (!this.config.groupingAction) {
                    return;
                }

                this.handleAction({
                    ...this.config.groupingAction,
                    url: window.decodeURIComponent(this.config.groupingAction.url).replace(':grouping-value', groupingValue),
                });
            },

            async handleActionsComponentAction(action, event) {
                await this.handleAction(action, event);

                if (this.config.deselectAfterAction) {
                    this.deselectAllRows();
                }
            },

            async fetchJob() {
                let job;

                try {
                    job = (await axios(this.config.jobUrl)).data;
                } catch (error) {
                    return this.handleErrorResponse(error);
                }

                await this.awaitJob(job.id, null, null, false, true);

                this.resolvedUrl = job.on_complete;
            },
        },
    };
</script>
